import { Formik, FormikConfig, FormikHelpers } from 'formik';
import React from 'react';
import { defaultMemoize } from 'reselect';

import { useSettingsCoordinator } from '~settings-coordinator/SettingsCoordinatorHook';
import {
  normalizeSectionSpec,
  SectionContainerBaseProps,
  SettingsSectionList,
} from '~settings-coordinator/SettingsSectionList';

type AdditionalProps = {
  availability?: string;
  siteId?: string;
}
export type SettingsFormikValues<SettingsType extends object> =
  Pick<SectionContainerBaseProps<SettingsType>, 'settings'>
  & AdditionalProps;

export type SettingsFormikContainerProps<SettingsType extends object> = SectionContainerBaseProps<SettingsType>
  & AdditionalProps
  & {
  formName: string;
  onSubmit?: (settings: SettingsType, availability: string, actions: FormikHelpers<SettingsFormikValues<SettingsType>>) => Promise<SettingsFormikValues<SettingsType> | void>;
  onResetToDefaults?: () => Promise<any>;
};

/**
 * create memoized formik values to be used with the HOC
 */
const mapPropsToValues = defaultMemoize(<SettingsType,>(settings: SettingsType, availability: string, siteId: string) => ({
  settings,
  availability,
  siteId
}));

export function SettingsFormikContainer<SettingsType extends object>(
  props: React.PropsWithChildren<SettingsFormikContainerProps<SettingsType> & Pick<FormikConfig<SettingsType>, 'enableReinitialize'>>
): JSX.Element {
  const initValues = mapPropsToValues(props.settings, props.availability, props.siteId);
  const {onSubmit, enableReinitialize, ...rest} = props;

  const handleSubmit = React.useCallback(async function handleSubmit(values: SettingsFormikValues<any>, actions: FormikHelpers<SettingsFormikValues<any>>) {

    // call per-section onSubmit to transform settings
    const transformedSettings = props.sections
      ?.map(normalizeSectionSpec)
      ?.reduce((settings, [Section]) =>
          Section.transformSettingsBeforeSubmit?.(settings) ?? settings,
        values.settings);

    // Submit settings as-is. Components using useMigratedFormikPrefix write to
    // settings.siteSettingsV2.* based on metadata category, so the structure is already correct.
    // - FF OFF: No siteSettingsV2 property exists
    // - FF ON: siteSettingsV2 contains flat key/value pairs matching the V2 API format
    const override = await onSubmit?.(transformedSettings, values.availability, actions) as any;

    // Sets the new values as the initial form values
    actions.resetForm({values: override ?? values});
  }, [onSubmit, props.sections]);

  return <Formik
    initialValues={initValues}
    onSubmit={handleSubmit}
    enableReinitialize={enableReinitialize}
  >
    <SettingsFormikContainerInternal {...rest}/>
  </Formik>;
}

/**
 * A wrapper around sections list. Its purpose is to use a hook to register with the settings coordinator
 */
function SettingsFormikContainerInternal<SettingsType extends object, Props extends SettingsFormikContainerProps<SettingsType>>(props: React.PropsWithChildren<Props>) {
  // Pull out children so they don't get rendered twice
  const {sections, settings, children, formName, onResetToDefaults, ...rest} = props;

  useSettingsCoordinator(formName, onResetToDefaults);
  return (
    <>
      {children} {/* This allows rendering custom components that are not in the registry */}
      <SettingsSectionList
        sections={sections}
        settings={settings}
        {...rest}
      />
    </>
  );
}

